import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGetBroker } from "../api";
import { useAuth } from "../components/AuthContext";

function money(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}
function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}

function section(title, children) {
  return (
    <div style={{ marginTop: 18, padding: 16, border: "1px solid #e5e5e5", borderRadius: 12, background: "white" }}>
      <div style={{ fontWeight: 900, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function daysAgo(dt) {
  if (!dt) return null;
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function badgeStyle(kind) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    border: "1px solid #e5e7eb",
  };

  if (kind === "OK") return { ...base, background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" };
  if (kind === "NOT_OK") return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
  if (kind === "BID") return { ...base, background: "#eff6ff", color: "#1e40af", borderColor: "#bfdbfe" };
  if (kind === "BID_ALERT") return { ...base, background: "#fef3c7", color: "#92400e", borderColor: "#fde68a" };
  return { ...base, background: "#f3f4f6", color: "#374151", borderColor: "#e5e7eb" };
}

const STATUS_LABELS = {
  DRAFT: { label: "Draft", color: "#6b7280", bg: "#f3f4f6" },
  CREATED: { label: "Created", color: "#6b7280", bg: "#f3f4f6" },
  POSTED: { label: "Posted", color: "#2563eb", bg: "#dbeafe" },
  TENDERED: { label: "Awarded", color: "#059669", bg: "#d1fae5" },
  ACCEPTED: { label: "Accepted", color: "#7c3aed", bg: "#ede9fe" },
  PICKED_UP: { label: "Picked Up", color: "#ea580c", bg: "#fff7ed" },
  IN_TRANSIT: { label: "In Transit", color: "#0891b2", bg: "#cffafe" },
  IN_PROGRESS: { label: "In Transit", color: "#0891b2", bg: "#cffafe" },
  DELIVERED: { label: "Delivered", color: "#16a34a", bg: "#dcfce7" },
  COMPLETED: { label: "Completed", color: "#065f46", bg: "#d1fae5" },
  CLOSED: { label: "Closed", color: "#065f46", bg: "#d1fae5" },
  CANCELLED: { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_LABELS[status] || { label: status, color: "#6b7280", bg: "#f3f4f6" };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 800,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}22`,
    }}>
      {cfg.label}
    </span>
  );
}

function ComplianceBadge({ carrierProfile }) {
  const overall = String(carrierProfile?.fmcsaOverall || "UNKNOWN").toUpperCase();
  const checkedAt = carrierProfile?.fmcsaCheckedAt || null;

  const label = overall === "OK" ? "Compliance: OK" : overall === "NOT_OK" ? "Compliance: NOT OK" : "Compliance: UNKNOWN";

  const ago = daysAgo(checkedAt);
  const sub =
    checkedAt && ago !== null
      ? ago === 0
        ? "checked today"
        : `checked ${ago}d ago`
      : "not checked";

  return (
    <span style={badgeStyle(overall === "OK" ? "OK" : overall === "NOT_OK" ? "NOT_OK" : "UNKNOWN")} title={checkedAt ? `FMCSA checked at ${fmt(checkedAt)}` : "No FMCSA check recorded"}>
      {label}
      <span style={{ fontWeight: 700, opacity: 0.75 }}>• {sub}</span>
    </span>
  );
}

function BidBadge({ bidCount, lowestBidCents, rateAmountCents }) {
  if (!bidCount || bidCount === 0) {
    return <span style={badgeStyle("UNKNOWN")}>No Bids</span>;
  }

  // Calculate savings percentage if we have both rate and lowest bid
  const savings = rateAmountCents && lowestBidCents
    ? Math.round((1 - lowestBidCents / rateAmountCents) * 100)
    : null;

  const kind = bidCount >= 3 ? "BID" : "BID_ALERT";

  return (
    <span style={badgeStyle(kind)} title={`${bidCount} bid(s) received. Lowest: ${money(lowestBidCents)}`}>
      🏷️ {bidCount} Bid{bidCount !== 1 ? "s" : ""}
      <span style={{ fontWeight: 700, opacity: 0.85 }}>
        • Low: {money(lowestBidCents)}
        {savings !== null && savings > 0 && (
          <span style={{ color: "#059669", marginLeft: 4 }}>({savings}% below)</span>
        )}
      </span>
    </span>
  );
}

export default function BrokerLoads() {
  const nav = useNavigate();
  const { adminEmail } = useAuth();

  const [status, setStatus] = useState("");
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function refresh() {
    setErr("");
    setLoading(true);
    try {
      const q = status ? `?status=${encodeURIComponent(status)}` : "";
      const data = await apiGetBroker(`/broker/loads${q}`, { adminEmail });
      setLoads(data.loads || []);
    } catch (e) {
      setErr(e.message || String(e));
      setLoads([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={() => nav("/app/broker/create")} style={{ padding: "10px 14px", fontWeight: 900 }}>
          + Create Load
        </button>

        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: 10 }}>
          <option value="">All statuses</option>
          <option value="DRAFT">DRAFT</option>
          <option value="POSTED">POSTED</option>
          <option value="TENDERED">TENDERED</option>
          <option value="ACCEPTED">ACCEPTED</option>
          <option value="PICKED_UP">PICKED_UP</option>
          <option value="DELIVERED">DELIVERED</option>
          <option value="CLOSED">CLOSED</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>

        <button onClick={refresh} style={{ padding: "10px 14px", fontWeight: 900 }} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {err ? (
        <div style={{ marginTop: 14, padding: 12, background: "#ffecec", border: "1px solid #ffb3b3", borderRadius: 10 }}>
          {err}
        </div>
      ) : null}

      {section(
        `Loads (${loads.length})`,
        loading ? (
          <div>Loading…</div>
        ) : loads.length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {loads.map((l) => {
              const carrierProfile = l?.carrier?.carrierProfile || null;

              return (
                <div key={l.id} style={{ border: "1px solid #eee", borderRadius: 12, padding: 14 }}>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ fontWeight: 900 }}>{l.referenceNumber || l.id}</div>

                    <div style={{ fontSize: 13, color: "#666", display: "flex", alignItems: "center", gap: 8 }}>
                      <StatusBadge status={l.status} /> {l.equipmentType || "—"} • Rate {money(l.rateAmountCents)}
                      {l.distanceMiles ? (
                        <>
                          {" "}
                          • <b>{l.distanceMiles}</b> mi
                        </>
                      ) : null}
                    </div>

                    <div style={{ marginLeft: "auto", fontSize: 13, color: "#666" }}>Pickup {fmt(l.pickupStart)}</div>
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <b>From:</b> {l.originAddress || "—"} <span style={{ margin: "0 10px", color: "#999" }}>→</span>
                    <b>To:</b> {l.destAddress || "—"}
                  </div>

                  <div style={{ marginTop: 8, fontSize: 13, color: "#555", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <b>Shipper:</b> {l.shipper?.name || "—"} &nbsp;&nbsp; <b>Carrier:</b> {l.carrier?.name || "—"}
                    </div>

                    {/* Carrier compliance badge */}
                    {l.carrier ? (
                      <ComplianceBadge carrierProfile={carrierProfile} />
                    ) : (
                      <span style={badgeStyle("UNKNOWN")}>Compliance: N/A</span>
                    )}
                  </div>

                  {/* Bid summary row */}
                  <div style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <BidBadge
                      bidCount={l.bidCount}
                      lowestBidCents={l.lowestBidCents}
                      rateAmountCents={l.rateAmountCents}
                    />
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => nav(`${l.id}`)} style={{ padding: "10px 14px", fontWeight: 900 }}>
                      Open
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ color: "#666" }}>No loads found.</div>
        )
      )}
    </div>
  );
}
